import { forwardRef, useEffect } from 'react';
import type {
    ComponentPropsWithRef,
    ComponentPropsWithoutRef,
    ReactNode,
    ChangeEvent,
    ElementType
} from 'react';
import clsx from 'clsx';
import useTranslation from 'next-translate/useTranslation';
import {
    FileUploaderProvider,
    useFileUploaderContext
} from '@/utils/hooks/context/useFileUploader';

// Components
import type { ButtonProps } from '@/components/base/Button';
import Button from '@/components/base/Button';
import Icon from '@/components/base/Icon';
import InfoBanner from '@/components/base/InfoBanner';

const ButtonLabelClass =
    'flex items-center cursor-pointer justify-center leading-none max-w-full transition duration-100 rounded-md outline-none focus:outline-none bg-icoBlue-100 dark:bg-icoBlue/20 text-icoBlue focus:ring-2 focus:ring-icoBlue-200 focus:ring-offset-2';

const LabelSize = {
    sm: 'h-8 px-3 min-w-[6.25rem] text-xs',
    md: 'p-5 min-w-150 text-sm'
} as const;

type FileUploaderType = ComponentPropsWithRef<'input'> & {
    handleOnFileChange: (_: File) => void;
    inputId?: string;
    minWidth: number;
    minHeight: number;
    maxSize: number;
    allowedExtensions: string[];
    children: ReactNode;
};

const FileUploaderWrapper = forwardRef<HTMLInputElement, FileUploaderType>(
    (
        {
            handleOnFileChange,
            inputId = 'input-file',
            minWidth = null,
            minHeight = null,
            maxSize = null, // In KiloBytes
            allowedExtensions = [],
            children,
            ...restProps
        },
        ref
    ) => (
        <FileUploaderProvider
            value={{
                inputId,
                constraints: {
                    minWidth,
                    minHeight,
                    maxSize,
                    allowedExtensions
                }
            }}>
            {children}
            <FileUploader.HiddenInput
                handleOnFileChange={handleOnFileChange}
                ref={ref}
                {...restProps}
            />
        </FileUploaderProvider>
    )
);

FileUploaderWrapper.displayName = 'FileUploader';

type ButtonUploadProps = ComponentPropsWithoutRef<'label'> & {
    size?: keyof typeof LabelSize;
    disabled?: boolean;
    icon: string;
};
const ButtonUpload = ({
    children,
    size = 'md',
    className = '',
    icon = '',
    disabled = false,
    ...restProps
}: ButtonUploadProps) => {
    const { fileUploaderData } = useFileUploaderContext();

    return (
        <label
            className={clsx(ButtonLabelClass, LabelSize[size], className, {
                'pointer-events-none cursor-not-allowed bg-icoGray-300 text-icoGray-500 dark:bg-icoDarkMode-beton':
                    disabled
            })}
            htmlFor={fileUploaderData?.inputId}
            {...restProps}>
            {icon ? (
                <Icon icon={icon} className={clsx('mr-3', { 'text-base': size === 'md' })} />
            ) : null}
            {children}
        </label>
    );
};

type ImagePreviewProps = ComponentPropsWithoutRef<'image'> & {
    as?: ElementType;
    children?: ReactNode;
    className?: string;
    url?: string;
    objectFit?: 'object-cover' | 'object-contain';
};

const ImagePreview = ({
    as: Component = ImageSimplePreview,
    children,
    className,
    url = '',
    ...restProps
}: ImagePreviewProps) => {
    const { fileUploaderData, dispatch } = useFileUploaderContext();

    useEffect(() => {
        if (url) {
            dispatch({ type: 'UPDATE_FILE', payload: { preview: url, name: '' } });
        }
    }, [dispatch, url]);
    return (
        <>
            {fileUploaderData?.file?.preview ? (
                <Component
                    url={fileUploaderData?.file?.preview}
                    name={fileUploaderData?.file?.name}
                    className={className}
                    {...restProps}>
                    {children}
                </Component>
            ) : null}
        </>
    );
};

type ImageSimplePreviewProps = {
    url?: string;
    name?: string;
    className?: string;
    children?: ReactNode;
    objectFit?: 'object-cover' | 'object-contain';
};

const ImageSimplePreview = ({
    url,
    name,
    className,
    children,
    objectFit
}: ImageSimplePreviewProps) => {
    const addClasses = `${objectFit} h-full`;
    return (
        <>
            {url ? (
                <div className="relative">
                    {children}
                    <div
                        className={clsx(
                            className,
                            'relative mr-3 overflow-hidden rounded-md',
                            !className && 'h-[3.375rem] w-[7.5rem]'
                        )}>
                        {/* Blob urls do not work with next/image */}
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                            data-testid="image-preview"
                            src={url}
                            alt={name}
                            className={clsx({
                                'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2':
                                    Boolean(!objectFit),
                                [addClasses]: Boolean(objectFit)
                            })}
                        />
                    </div>
                </div>
            ) : null}
        </>
    );
};

export const Link = <T extends ElementType>(
    props: {
        as?: T;
    } & ComponentPropsWithoutRef<ElementType extends T ? 'a' : T>
) => {
    const { as: Comp = 'a', ...rest } = props;
    return <Comp {...rest}></Comp>;
};

type RemoveButtonProps<TAs extends ElementType> = ButtonProps<TAs> &
    Pick<FileUploaderType, 'handleOnFileChange'> & {
        setValue?: (el: string, value: string) => void;
        inputName?: string;
    } & DistributiveOmit<ComponentPropsWithoutRef<ElementType extends TAs ? 'button' : TAs>, 'as'>;

const RemoveButton = <TAs extends ElementType>(props: RemoveButtonProps<TAs>) => {
    const {
        as: Component = Button,
        children,
        setValue,
        inputName,
        handleOnFileChange,
        ...restProps
    } = props;

    const { dispatch, fileUploaderData } = useFileUploaderContext();
    const resetInput = () => {
        dispatch({ type: 'RESET_FILE' });
        if (setValue && typeof setValue === 'function') {
            setValue(inputName, '');
        }
        // reset input value so that the user can reupload the same image multiple times consecutively
        const input = document.getElementById(fileUploaderData.inputId) as HTMLInputElement;
        input.value = '';
        handleOnFileChange(null);
    };

    return (
        <Component {...restProps} onClick={resetInput}>
            {children}
        </Component>
    );
};

type NoticeProps = {
    displayConstraintsNotice?: boolean;
    children?: ReactNode;
    className?: string;
};

const Notice = ({ displayConstraintsNotice = true, children, className }: NoticeProps) => {
    const { t } = useTranslation('commons');
    const { fileUploaderData } = useFileUploaderContext();
    return (
        <div className={clsx(className, 'text-xs text-icoGray-500 dark:text-icoDarkMode-wolf')}>
            {displayConstraintsNotice &&
            (fileUploaderData?.constraints?.minWidth ||
                fileUploaderData?.constraints?.minHeight ||
                fileUploaderData?.constraints?.maxSize ||
                (fileUploaderData?.constraints?.allowedExtensions &&
                    fileUploaderData?.constraints?.allowedExtensions.length > 0)) ? (
                <>
                    <p>{t('file_uploader.constraints.your_file_must')}</p>
                    <ul>
                        {fileUploaderData?.constraints?.minWidth &&
                        fileUploaderData?.constraints?.minHeight ? (
                            <li>
                                -{' '}
                                {t('file_uploader.constraints.dimensions', {
                                    minWidth: fileUploaderData?.constraints?.minWidth,
                                    minHeight: fileUploaderData?.constraints?.minHeight
                                })}
                            </li>
                        ) : (
                            <>
                                {fileUploaderData?.constraints?.minWidth && (
                                    <li>
                                        -{' '}
                                        {t('file_uploader.constraints.min_width', {
                                            minWidth: fileUploaderData?.constraints?.minWidth
                                        })}
                                    </li>
                                )}
                                {fileUploaderData?.constraints?.minHeight && (
                                    <li>
                                        -{' '}
                                        {t('file_uploader.constraints.min_height', {
                                            minHeight: fileUploaderData?.constraints?.minHeight
                                        })}
                                    </li>
                                )}
                            </>
                        )}
                        {fileUploaderData?.constraints?.maxSize && (
                            <li>
                                -{' '}
                                {t('file_uploader.constraints.max_size', {
                                    maxSize:
                                        Math.round(
                                            (fileUploaderData.constraints.maxSize / 1024) * 100
                                        ) / 100
                                })}
                            </li>
                        )}
                        {fileUploaderData?.constraints?.allowedExtensions &&
                            fileUploaderData?.constraints?.allowedExtensions.length > 0 && (
                                <li>
                                    -{' '}
                                    {t('file_uploader.constraints.allowed_extensions', {
                                        allowedExtensions:
                                            fileUploaderData.constraints.allowedExtensions.join(
                                                ', '
                                            )
                                    })}
                                </li>
                            )}
                    </ul>
                </>
            ) : null}
            {children}
        </div>
    );
};

type HiddenInputProps = ComponentPropsWithRef<'input'> &
    Pick<FileUploaderType, 'handleOnFileChange'>;

const HiddenInput = forwardRef<HTMLInputElement, HiddenInputProps>(
    ({ handleOnFileChange, ...restProps }, ref) => {
        const { fileUploaderData, dispatch } = useFileUploaderContext();

        const allowedExtensionsInline = fileUploaderData.constraints.allowedExtensions.join(', ');

        const areFileDimensionsValid = (src: string) => {
            // eslint-disable-next-line no-undef
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = () => {
                    let isValid = true;
                    if (fileUploaderData.constraints?.minWidth) {
                        if (img.width < fileUploaderData.constraints?.minWidth) {
                            dispatch({
                                type: 'UPDATE_ERROR',
                                payload: {
                                    type: 'min_width',
                                    current: img.width,
                                    required: fileUploaderData.constraints?.minWidth
                                }
                            });
                            isValid = false;
                        }
                    }
                    if (isValid && fileUploaderData.constraints?.minHeight) {
                        if (img.height < fileUploaderData.constraints?.minHeight) {
                            dispatch({
                                type: 'UPDATE_ERROR',
                                payload: {
                                    type: 'min_height',
                                    current: img.height,
                                    required: fileUploaderData.constraints?.minHeight
                                }
                            });
                            isValid = false;
                        }
                    }
                    resolve(isValid);
                };
                img.onerror = reject;
                img.src = src;
            });
        };

        const isFileValid = async (fileRaw: File, fileBase64: string) => {
            // Check file size
            if (fileUploaderData.constraints?.maxSize) {
                const sizeInKb = fileRaw.size / 1024;
                if (sizeInKb > fileUploaderData.constraints?.maxSize) {
                    dispatch({
                        type: 'UPDATE_ERROR',
                        payload: {
                            type: 'max_size',
                            current: Math.round((sizeInKb / 1024) * 100) / 100,
                            required:
                                Math.round((fileUploaderData.constraints?.maxSize / 1024) * 100) /
                                100
                        }
                    });
                    return false;
                }
            }

            // Check file extension
            if (fileUploaderData.constraints?.allowedExtensions.length > 0) {
                const fileExtension = `.${fileRaw.name.split('.').slice(-1).pop()}`;
                if (
                    !fileUploaderData.constraints?.allowedExtensions.includes(
                        fileExtension.toLowerCase()
                    )
                ) {
                    dispatch({
                        type: 'UPDATE_ERROR',
                        payload: {
                            type: 'allowed_extensions',
                            current: fileExtension,
                            required: allowedExtensionsInline
                        }
                    });
                    return false;
                }
            }

            // Check file width and/or height
            if (fileUploaderData.constraints?.minWidth || fileUploaderData.constraints?.minHeight) {
                if (!(await areFileDimensionsValid(fileBase64))) {
                    return false;
                }
            }

            return true;
        };

        const handleInputFileChange = async (e: ChangeEvent) => {
            const input = e.target as HTMLInputElement;

            if (input.files.length) {
                dispatch({
                    type: 'RESET_ERROR'
                });

                const file = input.files[0];

                const fileBase64 = URL.createObjectURL(file);

                try {
                    const isValid = await isFileValid(file, fileBase64);
                    if (isValid) {
                        dispatch({
                            type: 'UPDATE_FILE',
                            payload: {
                                preview: fileBase64,
                                raw: file,
                                name: file.name
                            }
                        });

                        handleOnFileChange(file);
                    } else {
                        const inputElement = document.getElementById(
                            fileUploaderData?.inputId
                        ) as HTMLInputElement;
                        // reset input to prevent cache behavior that prevent the user to reupload the same file twice
                        inputElement.value = '';
                    }
                } catch (e) {
                    dispatch({
                        type: 'UPDATE_ERROR',
                        payload: {
                            type: 'checking_process',
                            current: null,
                            required: null
                        }
                    });
                }
            }
        };

        return (
            <input
                id={fileUploaderData?.inputId}
                data-testid="upload-input"
                ref={ref}
                type="file"
                className="absolute h-0 w-0 opacity-0"
                accept={allowedExtensionsInline}
                {...restProps}
                onChange={handleInputFileChange}
            />
        );
    }
);
HiddenInput.displayName = 'HiddenInput';

type ErrorBannerProps = {
    className: string;
};
const ErrorBanner = ({ className }: ErrorBannerProps) => {
    const { t } = useTranslation('errors');
    const { fileUploaderData, dispatch } = useFileUploaderContext();

    if (fileUploaderData?.error?.type) {
        return (
            <InfoBanner
                title={t('commons:error')}
                message={t(`error_file_uploader.${fileUploaderData.error.type}`, {
                    [fileUploaderData.error.type]: fileUploaderData.error.required
                })}
                className={clsx(className, 'w-full')}
                data-testid="file-uploader-error-banner"
                onClose={() => {
                    dispatch({ type: 'RESET_ERROR' });
                }}
            />
        );
    }

    return null;
};

type ErrorMessageProps = {
    additionalError?: string;
    closeAdditionalError?: () => void;
    className?: string;
};

const ErrorMessage = ({ additionalError, closeAdditionalError, className }: ErrorMessageProps) => {
    const { t } = useTranslation('errors');

    const { fileUploaderData, dispatch } = useFileUploaderContext();

    if (fileUploaderData?.error?.type || additionalError) {
        return (
            <p
                className={clsx('flex text-icoRed', className)}
                data-testid="file-uploader-error-message">
                <span className="flex-1">
                    {fileUploaderData?.error?.type
                        ? t(`error_file_uploader.${fileUploaderData.error.type}`, {
                              [fileUploaderData.error.type]: fileUploaderData.error.required
                          })
                        : additionalError}
                </span>
                <button
                    aria-label={t('commons:close')}
                    type="button"
                    className="relative ml-8 h-4 w-4 overflow-hidden rounded-sm text-base focus:outline-none focus:ring-2"
                    onClick={() => {
                        fileUploaderData?.error?.type
                            ? dispatch({ type: 'RESET_ERROR' })
                            : closeAdditionalError();
                    }}>
                    <Icon icon="close" />
                    <p className="absolute text-transparent">{t('commons:close')}</p>
                </button>
            </p>
        );
    }

    return null;
};

const FileUploader = Object.assign({}, FileUploaderWrapper, {
    RemoveButton,
    ImagePreview,
    ButtonUpload,
    HiddenInput,
    Notice,
    ErrorBanner,
    ErrorMessage
});

export default FileUploader;
